import { Dashboard } from '@/types';
import { ReportSchedule } from '@/types/api/modules';

import { apiClient, ApiRequestConfig } from '../http';
import { favoriteService } from './favorites';
import { toDashboardMutationInput } from './resourceMutationPayloads';
import { createSanitizedResourceService, QueryParams } from './resources';

type DashboardMutationInput = ReturnType<typeof toDashboardMutationInput>;

export type CreateDashboardConfig = ApiRequestConfig<DashboardMutationInput, { template?: string }>;
export type DashboardReportScheduleResponse = {
    schedule: ReportSchedule | null;
    reports: Dashboard['reports'];
};

const base = createSanitizedResourceService<Dashboard, DashboardMutationInput, DashboardMutationInput>(
    '/api/dashboards',
    toDashboardMutationInput,
    toDashboardMutationInput,
);

export const dashboardService = {
    ...base,
    create: (input: Partial<Dashboard>, config?: CreateDashboardConfig) =>
        apiClient.post<Dashboard, DashboardMutationInput, { template?: string }>(
            '/api/dashboards',
            toDashboardMutationInput(input),
            config,
        ),
    getPublicById: (id: string) => apiClient.get<Dashboard>(`/public/dashboards/${id}`, { mode: 'unauthorized' }),
    list: <TParams extends QueryParams = QueryParams>(params?: TParams) => base.list(params),
    getSchedule: (id: string) => apiClient.get<DashboardReportScheduleResponse>(`/api/dashboards/${id}/schedule`),
    updateSchedule: (id: string, schedule: ReportSchedule) =>
        apiClient.put<DashboardReportScheduleResponse, ReportSchedule>(`/api/dashboards/${id}/schedule`, schedule),
    testSchedule: (id: string, schedule: ReportSchedule) =>
        apiClient.post<{ schedule: ReportSchedule }, { schedule: ReportSchedule }>(
            `/api/dashboards/${id}/schedule/test`,
            {
                schedule,
            },
        ),
    setArchived: (id: string, archived: boolean) => base.update(id, { archived }),
    setFavorite: (id: string, favorited: boolean) => favoriteService.set<Dashboard>('dashboards', id, favorited),
    setLocked: (id: string, locked: boolean) => base.update(id, { locked }),
};
